import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { db } from '../lib/firebase';
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore';
import {
  Briefcase, MapPin, DollarSign, Clock, Trash2, Pencil, Search, X, Loader2, Plus, UserCheck
} from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '../components/ui/alert-dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusColors = {
  pending: 'text-yellow-400 bg-yellow-400/10 border-yellow-400/20',
  assigned: 'text-blue-400 bg-blue-400/10 border-blue-400/20',
  in_progress: 'text-purple-400 bg-purple-400/10 border-purple-400/20',
  completed: 'text-green-400 bg-green-400/10 border-green-400/20',
  cancelled: 'text-red-400 bg-red-400/10 border-red-400/20',
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const AdminJobsTab = () => {
  const [jobs, setJobs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ customer_id: '', driver_id: '', pickup_location: '', dropoff_location: '', price: '', status: 'pending' });
  const [saving, setSaving] = useState(false);

  // Real-time listeners
  useEffect(() => {
    const unsubs = [];
    unsubs.push(onSnapshot(query(collection(db, 'jobs'), orderBy('created_at', 'desc')), (snap) => {
      setJobs(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }));
    unsubs.push(onSnapshot(collection(db, 'customers'), (snap) => {
      setCustomers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }));
    unsubs.push(onSnapshot(collection(db, 'drivers'), (snap) => {
      setDrivers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }));
    return () => unsubs.forEach(u => u());
  }, []);

  const getCustomerName = (id) => customers.find(c => c.id === id)?.name || id?.slice(0, 8) + '...';
  const getDriverName = (id) => drivers.find(d => d.id === id)?.name || (id ? id.slice(0, 8) + '...' : 'Unassigned');

  const filtered = jobs
    .filter(j => filter === 'all' || j.status === filter)
    .filter(j => {
      if (!search) return true;
      const q = search.toLowerCase();
      const cName = getCustomerName(j.customer_id).toLowerCase();
      const dName = getDriverName(j.driver_id).toLowerCase();
      return cName.includes(q) || dName.includes(q) || j.pickup_location?.toLowerCase().includes(q) || j.dropoff_location?.toLowerCase().includes(q);
    });

  const resetForm = () => { setForm({ customer_id: '', driver_id: '', pickup_location: '', dropoff_location: '', price: '', status: 'pending' }); setEditId(null); setShowForm(false); };

  const handleEdit = (j) => {
    setForm({ customer_id: j.customer_id || '', driver_id: j.driver_id || '', pickup_location: j.pickup_location, dropoff_location: j.dropoff_location, price: String(j.price || ''), status: j.status });
    setEditId(j.id); setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form, price: parseFloat(form.price) || 0 };
    if (!payload.driver_id) delete payload.driver_id;
    try {
      if (editId) {
        await axios.put(`${API}/firestore/jobs/${editId}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API}/firestore/jobs`, payload, { withCredentials: true });
      }
      resetForm();
    } catch (err) {
      console.error('Save failed:', err.response?.data?.detail || err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try { await axios.delete(`${API}/firestore/jobs/${id}`, { withCredentials: true }); } catch (err) { console.error('Delete failed:', err); }
  };

  const handleStatusChange = async (id, newStatus) => {
    try { await axios.put(`${API}/firestore/jobs/${id}/status`, { status: newStatus }, { withCredentials: true }); } catch (err) { console.error('Status update failed:', err); }
  };

  const handleAssignDriver = async (jobId, driverId) => {
    if (!driverId) return;
    try { await axios.post(`${API}/firestore/jobs/${jobId}/assign`, { driver_id: driverId }, { withCredentials: true }); } catch (err) { console.error('Assign failed:', err); }
  };

  const availableDrivers = drivers.filter(d => d.status === 'available' || d.status === 'off_duty');

  return (
    <div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center">
            <Briefcase className="w-5 h-5 text-purple-400" />
          </div>
          <div>
            <h2 className="font-heading text-xl text-white">Jobs</h2>
            <p className="text-slate-500 text-xs">{jobs.length} total &middot; Firestore real-time</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..."
              className="pl-10 pr-4 py-2 bg-brand-surface border border-white/10 rounded-lg text-white placeholder-slate-600 text-sm focus:border-brand-red outline-none w-48"
              data-testid="job-search" />
          </div>
          <button onClick={() => { resetForm(); setShowForm(true); }}
            className="flex items-center gap-2 px-4 py-2 bg-brand-red hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors"
            data-testid="add-job-btn">
            <Plus className="w-4 h-4" /> New Job
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap mb-6">
        {['all', 'pending', 'assigned', 'in_progress', 'completed', 'cancelled'].map(s => (
          <button key={s} onClick={() => setFilter(s)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${filter === s ? 'bg-brand-red text-white' : 'bg-white/5 text-slate-400 hover:bg-white/10'}`}
            data-testid={`job-filter-${s}`}>
            {s === 'all' ? 'All' : s.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
          </button>
        ))}
      </div>

      {/* Form */}
      {showForm && (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
          className="bg-brand-surface rounded-xl border border-white/10 p-6 mb-6" data-testid="job-form">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-white">{editId ? 'Edit Job' : 'New Job'}</h3>
            <button onClick={resetForm} className="text-slate-500 hover:text-white"><X className="w-5 h-5" /></button>
          </div>
          <form onSubmit={handleSubmit} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <select value={form.customer_id} onChange={e => setForm(p => ({ ...p, customer_id: e.target.value }))} required
              className="px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white focus:border-brand-red outline-none text-sm appearance-none" data-testid="job-customer-select">
              <option value="" disabled className="bg-brand-navy">Select customer</option>
              {customers.map(c => <option key={c.id} value={c.id} className="bg-brand-navy">{c.name}</option>)}
            </select>
            <select value={form.driver_id} onChange={e => setForm(p => ({ ...p, driver_id: e.target.value }))}
              className="px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white focus:border-brand-red outline-none text-sm appearance-none" data-testid="job-driver-select">
              <option value="" className="bg-brand-navy">No driver (unassigned)</option>
              {drivers.map(d => <option key={d.id} value={d.id} className="bg-brand-navy">{d.name} ({d.status})</option>)}
            </select>
            <input type="number" step="0.01" value={form.price} onChange={e => setForm(p => ({ ...p, price: e.target.value }))} placeholder="Price ($)" required
              className="px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-slate-600 focus:border-brand-red outline-none text-sm" data-testid="job-price-input" />
            <input type="text" value={form.pickup_location} onChange={e => setForm(p => ({ ...p, pickup_location: e.target.value }))} placeholder="Pickup location" required
              className="px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-slate-600 focus:border-brand-red outline-none text-sm" data-testid="job-pickup-input" />
            <input type="text" value={form.dropoff_location} onChange={e => setForm(p => ({ ...p, dropoff_location: e.target.value }))} placeholder="Dropoff location" required
              className="px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-slate-600 focus:border-brand-red outline-none text-sm" data-testid="job-dropoff-input" />
            <select value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value }))}
              className="px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white focus:border-brand-red outline-none text-sm appearance-none" data-testid="job-status-input">
              {['pending', 'assigned', 'in_progress', 'completed', 'cancelled'].map(s => (
                <option key={s} value={s} className="bg-brand-navy">{s.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</option>
              ))}
            </select>
            <div className="sm:col-span-2 lg:col-span-3 flex justify-end">
              <button type="submit" disabled={saving}
                className="flex items-center gap-2 px-6 py-2.5 bg-brand-red hover:bg-red-700 text-white rounded-lg text-sm font-medium disabled:opacity-50 transition-colors" data-testid="job-save-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                {editId ? 'Update' : 'Create Job'}
              </button>
            </div>
          </form>
        </motion.div>
      )}

      {/* List */}
      {filtered.length === 0 ? (
        <div className="text-center py-20"><Briefcase className="w-12 h-12 text-slate-600 mx-auto mb-4" /><p className="text-slate-500">No jobs found</p></div>
      ) : (
        <div className="space-y-3">
          {filtered.map((j, i) => (
            <motion.div key={j.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.02 }}
              className="bg-brand-surface rounded-xl border border-white/10 p-5" data-testid={`job-row-${j.id}`}>
              <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${statusColors[j.status] || 'text-slate-400 bg-slate-400/10 border-slate-400/20'}`}>{j.status}</span>
                    <span className="text-white font-medium">{getCustomerName(j.customer_id)}</span>
                    <span className="text-slate-600 text-xs">{j.id.slice(0, 8)}...</span>
                  </div>
                  <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm">
                    <span className="text-green-400 flex items-center gap-1"><MapPin className="w-3 h-3" />{j.pickup_location}</span>
                    <span className="text-brand-red flex items-center gap-1"><MapPin className="w-3 h-3" />{j.dropoff_location}</span>
                    <span className="text-white flex items-center gap-1"><DollarSign className="w-3 h-3" />${j.price?.toFixed(2)}</span>
                    <span className="text-blue-400 flex items-center gap-1"><UserCheck className="w-3 h-3" />{getDriverName(j.driver_id)}</span>
                    <span className="text-slate-600 flex items-center gap-1"><Clock className="w-3 h-3" />{formatDate(j.created_at)}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
                  {/* Assign driver */}
                  <Select value={j.driver_id || ''} onValueChange={(val) => handleAssignDriver(j.id, val)}>
                    <SelectTrigger className="w-[130px] bg-white/5 border-white/10 text-white text-xs" data-testid={`job-assign-driver-${j.id}`}>
                      <SelectValue placeholder="Assign driver" />
                    </SelectTrigger>
                    <SelectContent className="bg-brand-navy border-white/10">
                      {drivers.map(d => (
                        <SelectItem key={d.id} value={d.id} className="text-white hover:bg-white/10">{d.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {/* Status */}
                  <Select value={j.status} onValueChange={(val) => handleStatusChange(j.id, val)}>
                    <SelectTrigger className="w-[120px] bg-white/5 border-white/10 text-white text-xs" data-testid={`job-status-select-${j.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-brand-navy border-white/10">
                      {['pending', 'assigned', 'in_progress', 'completed', 'cancelled'].map(s => (
                        <SelectItem key={s} value={s} className="text-white hover:bg-white/10">{s.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <button onClick={() => handleEdit(j)} className="p-2 rounded-lg bg-white/5 text-slate-400 hover:text-white hover:bg-white/10 transition-colors" data-testid={`edit-job-${j.id}`}>
                    <Pencil className="w-4 h-4" />
                  </button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button className="p-2 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors" data-testid={`delete-job-${j.id}`}><Trash2 className="w-4 h-4" /></button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="bg-brand-navy border-white/10">
                      <AlertDialogHeader><AlertDialogTitle className="text-white">Delete Job</AlertDialogTitle>
                        <AlertDialogDescription className="text-slate-400">Permanently delete this job?</AlertDialogDescription></AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="bg-white/5 text-white border-white/10 hover:bg-white/10">Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => handleDelete(j.id)} className="bg-red-600 text-white hover:bg-red-700">Delete</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminJobsTab;
